import React, { useState } from 'react';
import { Search, X, Filter, IndianRupee, CheckCircle, AlertCircle, Plus, User, Calendar, CreditCard, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, SlidersHorizontal, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useBillingUtils } from '@/hooks/useBillingUtils';

interface BillingSearchFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  dateFilter?: string;
  onDateChange?: (date: string) => void;
  // Add date range props
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  // Add amount range props
  amountRange?: { min: string; max: string };
  onAmountRangeChange?: (range: { min: string; max: string }) => void;
  variant: 'patient' | 'clinic';
  className?: string;
  // Add props for filter feedback
  invoices?: any[];
  filteredInvoices?: any[];
}

export function BillingSearchFilters({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  dateFilter,
  onDateChange,
  dateRange,
  onDateRangeChange,
  amountRange: externalAmountRange,
  onAmountRangeChange,
  variant,
  className,
  invoices = [],
  filteredInvoices = []
}: BillingSearchFiltersProps) {
  const { formatDate } = useBillingUtils();
  const [showDateRange, setShowDateRange] = useState(false);
  
  // Enhanced search states
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  // Use external amount range or fallback to empty
  const amountRange = externalAmountRange || { min: '', max: '' };

  const searchPlaceholder = variant === 'patient' 
    ? "Search by invoice number, amount, service..."
    : "Search by patient name, invoice number, phone, amount...";

  // Generate search suggestions
  const generateSearchSuggestions = (term: string) => {
    if (!term.trim()) return [];
    
    const suggestions = new Set<string>();
    
    invoices.forEach(invoice => {
      // Patient name matches
      if (invoice.patient_name && invoice.patient_name.toLowerCase().includes(term.toLowerCase())) {
        suggestions.add(invoice.patient_name);
      }
      
      // Invoice number matches
      if (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(term.toLowerCase())) {
        suggestions.add(invoice.invoice_number);
      }
      
      // Phone matches
      if (invoice.patient_phone && invoice.patient_phone.includes(term)) {
        suggestions.add(invoice.patient_phone);
      }
      
      // Doctor name matches
      if (invoice.doctor_name && invoice.doctor_name.toLowerCase().includes(term.toLowerCase())) {
        suggestions.add(invoice.doctor_name);
      }
    });
    
    return Array.from(suggestions).slice(0, 5);
  };

  // Enhanced search handlers
  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    
    if (value.trim()) {
      const suggestions = generateSearchSuggestions(value);
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = () => {
    if (searchTerm.trim() && !recentSearches.includes(searchTerm)) {
      setRecentSearches(prev => [searchTerm, ...prev.slice(0, 4)]);
    }
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSearchChange(suggestion);
    setShowSuggestions(false);
    if (!recentSearches.includes(suggestion)) {
      setRecentSearches(prev => [suggestion, ...prev.slice(0, 4)]);
    }
  };

  const handleDateRangeReset = () => {
    if (onDateRangeChange) {
      onDateRangeChange({ from: null, to: null });
    }
    setShowDateRange(false);
  };

  const handleClearAllFilters = () => {
    onSearchChange('');
    onStatusChange('all');
    if (onDateRangeChange) {
      onDateRangeChange({ from: null, to: null });
    }
    if (onDateChange) {
      onDateChange('all');
    }
    onAmountRangeChange?.({ min: '', max: '' });
    setShowDateRange(false);
    setShowSuggestions(false);
  };

  const getDateRangeLabel = () => {
    if (!dateRange?.from && !dateRange?.to) {
      return "Date Range";
    }
    if (dateRange.from && dateRange.to) {
      return `${formatDate(dateRange.from.toISOString())} - ${formatDate(dateRange.to.toISOString())}`;
    }
    if (dateRange.from) {
      return `From ${formatDate(dateRange.from.toISOString())}`;
    }
    if (dateRange.to) {
      return `Until ${formatDate(dateRange.to.toISOString())}`;
    }
    return "Date Range";
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || 
    (dateFilter && dateFilter !== 'all') || 
    (dateRange?.from || dateRange?.to) ||
    amountRange.min || amountRange.max;

      return (
      <Card className={cn("medical-card", className)}>
        <CardContent className="p-3 sm:p-4 space-y-3">
        {/* Top Row: Enhanced Search + Status Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {/* Enhanced Search Input */}
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              id="billing-search"
              name="billing-search"
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
                if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => {
                if (searchTerm && searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              className={cn(
                "pl-10 pr-10 h-9 text-sm font-medium transition-colors shadow-sm bg-background",
                variant === 'patient' 
                  ? "border border-input focus:border-primary focus:ring-1 focus:ring-primary/20" 
                  : "border-2 border-border focus:border-primary"
              )}
            />
            
            {/* Clear Search */}
            {searchTerm && (
              <button
                onClick={() => {
                  onSearchChange('');
                  setShowSuggestions(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Search Suggestions Dropdown */}
            {showSuggestions && (searchSuggestions.length > 0 || recentSearches.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50">
                <div className="p-2 space-y-1">
                  {/* Search Suggestions */}
                  {searchSuggestions.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground px-2 py-1">Suggestions</div>
                      {searchSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm flex items-center gap-2"
                        >
                          <Search className="w-3 h-3 text-muted-foreground" />
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Recent Searches */}
                  {recentSearches.length > 0 && !searchTerm && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground px-2 py-1 border-t border-border mt-2 pt-2">Recent</div>
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(search)}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm flex items-center gap-2"
                        >
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {search}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Status Filter Buttons + Advanced Search Toggle */}
          <div className="flex items-center gap-1.5 sm:gap-2 justify-stretch sm:justify-start">
            <Button
              variant={statusFilter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onStatusChange(statusFilter === 'pending' ? 'all' : 'pending')}
              className={cn(
                "h-9 px-3 sm:px-4 font-medium flex-1 sm:flex-none min-w-20 shadow-sm",
                variant === 'patient' ? "border" : "border-2"
              )}
            >
              Unpaid
            </Button>
            <Button
              variant={statusFilter === 'paid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onStatusChange(statusFilter === 'paid' ? 'all' : 'paid')}
              className={cn(
                "h-9 px-3 sm:px-4 font-medium flex-1 sm:flex-none min-w-20 shadow-sm",
                variant === 'patient' ? "border" : "border-2"
              )}
            >
              Paid
            </Button>
            
            {/* Advanced Search Toggle - only for clinic variant */}
            {variant === 'clinic' && (
              <Button
                variant={showAdvancedSearch ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                className="h-9 px-3"
              >
                <SlidersHorizontal className="w-4 h-4 mr-1" />
                Filters
              </Button>
            )}
          </div>
        </div>

        {/* Advanced Search Panel - only for clinic variant */}
        {variant === 'clinic' && showAdvancedSearch && (
          <div className="border border-border rounded-lg p-4 bg-muted/20 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Amount Filters */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">Amount Filters</label>
                  {(amountRange.min || amountRange.max) && (
                    <button
                      onClick={() => onAmountRangeChange?.({ min: '', max: '' })}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear Amount
                    </button>
                  )}
                </div>
                
                {/* Amount Range Inputs */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="amount-min"
                      name="amount-min"
                      type="number"
                      placeholder="Min amount"
                      value={amountRange.min}
                      onChange={(e) => onAmountRangeChange?.({ ...amountRange, min: e.target.value })}
                      className="pl-7 h-9 text-sm"
                    />
                  </div>
                  <span className="text-muted-foreground text-sm">to</span>
                  <div className="relative flex-1">
                    <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="amount-max"
                      name="amount-max"
                      type="number"
                      placeholder="Max amount"
                      value={amountRange.max}
                      onChange={(e) => onAmountRangeChange?.({ ...amountRange, max: e.target.value })}
                      className="pl-7 h-9 text-sm"
                    />
                  </div>
                </div>
                
                {/* Quick Amount Buttons */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Under ₹1000', min: '', max: '1000' },
                    { label: '₹1000-5000', min: '1000', max: '5000' },
                    { label: '₹5000-10000', min: '5000', max: '10000' },
                    { label: 'Above ₹10000', min: '10000', max: '' }
                  ].map((range) => {
                    const isActive = amountRange.min === range.min && amountRange.max === range.max;
                    return (
                      <button
                        key={range.label}
                        onClick={() => onAmountRangeChange?.({ min: range.min, max: range.max })}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded-md border transition-colors",
                          isActive 
                            ? "bg-primary text-primary-foreground border-primary" 
                            : "border-border hover:bg-muted hover:border-primary/50"
                        )}
                      >
                        {range.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date Filters */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">Date Filters</label>
                  {((dateFilter && dateFilter !== 'all') || dateRange?.from || dateRange?.to) && (
                    <button
                      onClick={() => {
                        onDateChange?.('all');
                        handleDateRangeReset();
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear Date
                    </button>
                  )}
                </div>
                
                {/* Quick Date Buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: 'Today', value: 'today' },
                    { label: 'This Week', value: 'week' },
                    { label: 'This Month', value: 'month' }
                  ].map((period) => {
                    const isActive = (dateFilter || 'all') === period.value;
                    return (
                      <button
                        key={period.value}
                        onClick={() => {
                          if (!onDateChange) return;
                          const newFilter = isActive ? 'all' : period.value;
                          onDateChange(newFilter);
                          handleDateRangeReset();
                        }}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded-md border transition-colors",
                          isActive 
                            ? "bg-primary text-primary-foreground border-primary" 
                            : "border-border hover:bg-muted hover:border-primary/50"
                        )}
                      >
                        {period.label}
                      </button>
                    );
                  })}
                </div>
                
                {/* Custom Date Range */}
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">Custom Date Range</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">From Date</label>
                      <Input
                        type="date"
                        value={dateRange?.from ? dateRange.from.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          if (onDateRangeChange) {
                            onDateRangeChange({
                              from: e.target.value ? new Date(e.target.value) : null,
                              to: dateRange?.to || null
                            });
                          }
                          if (onDateChange) onDateChange('all'); // Reset quick filters
                        }}
                        className="h-9 text-sm [&::-webkit-calendar-picker-indicator]:opacity-100"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">To Date</label>
                      <Input
                        type="date"
                        value={dateRange?.to ? dateRange.to.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          if (onDateRangeChange) {
                            onDateRangeChange({
                              from: dateRange?.from || null,
                              to: e.target.value ? new Date(e.target.value) : null
                            });
                          }
                          if (onDateChange) onDateChange('all'); // Reset quick filters
                        }}
                        className="h-9 text-sm [&::-webkit-calendar-picker-indicator]:opacity-100"
                        min={dateRange?.from ? dateRange.from.toISOString().split('T')[0] : undefined}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Results Summary */}
        {hasActiveFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-medium">
                <Filter className="w-3 h-3" />
                <span>
                  Showing <span className="font-bold">{filteredInvoices.length}</span> of {invoices.length} invoices
                </span>
              </div>
              
              {/* Active Filter Tags */}
              <div className="flex flex-wrap items-center gap-1 text-xs">
                {searchTerm && (
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                    Search: "{searchTerm}"
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-700 capitalize">
                    Status: {statusFilter}
                  </span>
                )}
                {dateFilter && dateFilter !== 'all' && (
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                    Period: {dateFilter === 'today' ? 'Today' : dateFilter === 'week' ? 'This Week' : 'This Month'}
                  </span>
                )}
                {(dateRange?.from || dateRange?.to) && (
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                    Date: {dateRange?.from && dateRange?.to 
                      ? `${formatDate(dateRange.from.toISOString())} - ${formatDate(dateRange.to.toISOString())}`
                      : dateRange?.from 
                        ? `From ${formatDate(dateRange.from.toISOString())}`
                        : `Until ${formatDate(dateRange.to.toISOString())}`
                    }
                  </span>
                )}
                {(amountRange.min || amountRange.max) && (
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                    Amount: {amountRange.min && amountRange.max 
                      ? `₹${amountRange.min}-₹${amountRange.max}`
                      : amountRange.min 
                        ? `Above ₹${amountRange.min}`
                        : `Under ₹${amountRange.max}`
                    }
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="h-7 px-3 text-blue-700 dark:text-blue-300 hover:text-white hover:bg-blue-600 border border-blue-300 dark:border-blue-600 shrink-0 transition-colors"
            >
              <X className="w-3 h-3 mr-1" />
              Clear All
            </Button>
          </div>
        )}


      </CardContent>
    </Card>
  );
}

interface BillingSummaryCardsProps {
  totalRevenue: number;
  pendingAmount: number;
  totalInvoices: number;
  paidInvoices?: number;
  context: 'patient' | 'clinic';
  monthlyRevenue?: number;
  analytics?: {
    totalRevenue: number;
    monthlyRevenue: number;
    pendingAmount: number;
    totalInvoices: number;
    paidInvoices: number;
    pendingInvoices: number;
    averageInvoiceValue: number;
  };
  className?: string;
}

export function BillingSummaryCards({
  totalRevenue,
  pendingAmount,
  totalInvoices,
  paidInvoices,
  context,
  monthlyRevenue,
  analytics,
  className
}: BillingSummaryCardsProps) {
  const { formatCurrency } = useBillingUtils();

  if (context === 'patient') {
    // Patient context - compact inline layout
    return (
      <div className={cn("grid grid-cols-2 gap-3", className)}>
        {/* Total Revenue */}
        <div className="bg-gradient-to-r from-success/10 to-success/5 border border-success/20 rounded-lg p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-success/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-success" />
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">{formatCurrency(totalRevenue)}</div>
            <div className="text-xs text-muted-foreground">Total Revenue</div>
          </div>
        </div>

        {/* Outstanding Amount */}
        <div className="bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/20 rounded-lg p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-warning/20 flex items-center justify-center">
            <AlertCircle className="w-4 h-4 text-warning" />
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">{formatCurrency(pendingAmount)}</div>
            <div className="text-xs text-muted-foreground">Outstanding</div>
          </div>
        </div>
      </div>
    );
  }

  // Clinic context - compact 4-card layout
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3", className)}>
      {/* Total Revenue Card */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center">
            <IndianRupee className="w-3 h-3 text-primary" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              {formatCurrency(analytics?.totalRevenue || totalRevenue)}
            </div>
            <div className="text-xs text-muted-foreground">Total Revenue</div>
          </div>
        </div>
      </div>

      {/* Monthly Revenue Card */}
      <div className="bg-gradient-to-r from-success/10 to-success/5 border border-success/20 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-success/20 flex items-center justify-center">
            <CheckCircle className="w-3 h-3 text-success" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              {formatCurrency(analytics?.monthlyRevenue || monthlyRevenue || 0)}
            </div>
            <div className="text-xs text-muted-foreground">This Month</div>
          </div>
        </div>
      </div>

      {/* Outstanding Amount Card */}
      <div className="bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/20 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-warning/20 flex items-center justify-center">
            <AlertCircle className="w-3 h-3 text-warning" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              {formatCurrency(analytics?.pendingAmount || pendingAmount)}
            </div>
            <div className="text-xs text-muted-foreground">Outstanding</div>
          </div>
        </div>
      </div>

      {/* Collection Rate Card */}
      <div className="bg-gradient-to-r from-info/10 to-info/5 border border-border rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center">
            <IndianRupee className="w-3 h-3 text-primary" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              {analytics && analytics.totalInvoices > 0 
                ? `${(analytics.paidInvoices / analytics.totalInvoices * 100).toFixed(1)}%`
                : 'N/A'
              }
            </div>
            <div className="text-xs text-muted-foreground">Collection Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EmptyStateCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  variant: 'invoices' | 'search';
  className?: string;
}

export function EmptyStateCard({
  icon,
  title,
  description,
  actionButton,
  variant,
  className
}: EmptyStateCardProps) {
  return (
    <Card className={cn("medical-card border-2 border-dashed", className)}>
      <CardContent className="text-center py-8 px-6">
        <div className="w-16 h-16 mx-auto bg-muted/30 rounded-full flex items-center justify-center mb-4 shadow-inner">
          <div className="text-muted-foreground">
            {icon}
          </div>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm leading-relaxed">
          {description}
        </p>
        {actionButton && (
          <div className="space-y-3">
            <Button 
              onClick={actionButton.onClick}
              size="default"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 text-sm font-medium shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="w-4 h-4 mr-2" />
              {actionButton.label}
            </Button>
            <p className="text-xs text-muted-foreground">
              Get started by creating your first {variant === 'invoices' ? 'invoice' : 'record'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  className?: string;
}

export function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  className
}: PaginationProps) {
  // Calculate display info
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <Card className={cn("p-3 bg-primary/5 border-primary/20", className)}>
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Page info */}
          <div className="text-sm font-medium text-primary">
            Showing {startItem} to {endItem} of {totalItems} results • Page {currentPage} of {totalPages}
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-1">
            {/* First page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="h-9 w-9 p-0 shadow-sm hover:shadow-md transition-shadow disabled:opacity-50"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>

            {/* Previous page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-9 w-9 p-0 shadow-sm hover:shadow-md transition-shadow disabled:opacity-50"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-2">
              {getPageNumbers().map((pageNum, index) => (
                <React.Fragment key={index}>
                  {pageNum === '...' ? (
                    <span className="px-3 py-2 text-sm text-muted-foreground font-medium">...</span>
                  ) : (
                    <Button
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(pageNum as number)}
                      className={cn(
                        "h-9 w-9 p-0 text-sm font-semibold shadow-sm hover:shadow-md transition-all duration-200",
                        currentPage === pageNum 
                          ? "bg-primary text-primary-foreground ring-2 ring-primary/20 scale-105" 
                          : "hover:bg-primary/10 hover:border-primary/50"
                      )}
                    >
                      {pageNum}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Next page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-9 w-9 p-0 shadow-sm hover:shadow-md transition-shadow disabled:opacity-50"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            {/* Last page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="h-9 w-9 p-0 shadow-sm hover:shadow-md transition-shadow disabled:opacity-50"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Quick jump */}
          <div className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
            <span className="text-muted-foreground font-medium">Go to:</span>
            <Input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  onPageChange(page);
                }
              }}
              className="w-16 h-8 px-2 text-center text-sm font-semibold border-primary/30 focus:border-primary focus:ring-primary/20"
            />
            <span className="text-muted-foreground font-medium">of {totalPages}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Combined component that includes both search filters and empty state handling
interface BillingListContainerProps {
  // Search & Filter props
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  dateFilter?: string;
  onDateChange?: (date: string) => void;
  // Add date range props
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  // Add amount range props
  amountRange?: { min: string; max: string };
  onAmountRangeChange?: (range: { min: string; max: string }) => void;
  variant: 'patient' | 'clinic';
  
  // Data props
  invoices: any[];
  filteredInvoices: any[];
  
  // Pagination props
  currentPage?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (items: number) => void;
  
  // Empty state
  onCreateInvoice?: () => void;
  
  // Children
  children: React.ReactNode;
  className?: string;
}

export function BillingListContainer({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  dateFilter,
  onDateChange,
  dateRange,
  onDateRangeChange,
  amountRange,
  onAmountRangeChange,
  variant,
  invoices,
  filteredInvoices,
  currentPage = 1,
  itemsPerPage = 25,
  onPageChange,
  onItemsPerPageChange,
  onCreateInvoice,
  children,
  className
}: BillingListContainerProps) {
  const hasResults = filteredInvoices.length > 0;
  const hasAnyInvoices = invoices.length > 0;
  const isFiltered = searchTerm || statusFilter !== 'all' || (dateFilter && dateFilter !== 'all') || (dateRange?.from || dateRange?.to);
  
  // Calculate pagination if handlers are provided
  const totalPages = onPageChange ? Math.ceil(filteredInvoices.length / itemsPerPage) : 1;
  const startIndex = onPageChange ? (currentPage - 1) * itemsPerPage : 0;
  const endIndex = onPageChange ? startIndex + itemsPerPage : filteredInvoices.length;
  const shouldPaginate = onPageChange && onItemsPerPageChange && totalPages > 1;
  
  return (
    <div className={cn("space-y-2", className)}>
      {/* Search Filters */}
      <BillingSearchFilters
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusChange={onStatusChange}
        dateFilter={dateFilter}
        onDateChange={onDateChange}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        amountRange={amountRange}
        onAmountRangeChange={onAmountRangeChange}
        variant={variant}
        invoices={invoices}
        filteredInvoices={filteredInvoices}
      />
      
      {/* Content or Empty State */}
      {hasResults ? (
        <div className="space-y-2">
          {children}
          
          {/* Pagination Controls */}
          {shouldPaginate && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredInvoices.length}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
            />
          )}
        </div>
      ) : (
        <EmptyStateCard
          icon={<IndianRupee className="w-10 h-10 text-muted-foreground" />}
          title={hasAnyInvoices ? 'No invoices match your search' : 'No invoices found'}
          description={
            hasAnyInvoices 
              ? 'Try adjusting your search criteria or filters to find what you\'re looking for'
              : variant === 'patient'
                ? 'Create the first invoice for this patient to get started'
                : 'Get started by creating your first invoice for a patient'
          }
          actionButton={
            !hasAnyInvoices && onCreateInvoice 
              ? {
                  label: variant === 'patient' ? 'Create First Invoice' : 'Create Your First Invoice',
                  onClick: onCreateInvoice
                }
              : undefined
          }
          variant={isFiltered ? 'search' : 'invoices'}
        />
      )}
    </div>
  );
} 